import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaTimes } from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function AddFoodItem() {
  const navigate = useNavigate();
  const [subCategories, setSubCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState({
    availableFrom: false,
    availableUntil: false,
  });
  const [tempTime, setTempTime] = useState({
    availableFrom: '',
    availableUntil: '',
  });
  const [newFoodItem, setNewFoodItem] = useState({
    name: '',
    description: '',
    price: '',
    price_type: 'Per person',
    image: null,
    status: 'Available',
    subCategoryId: '',
    mealType: [],
    foodType: 'Veg',
    availableFrom: '',
    availableUntil: '',
    showEnhanceStay: false,
    refundable: false,
    showInCheckin: false,
    value: '',
    value_type: '',
  });

  // Fetch subcategories from API
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const subCategoriesResponse = await fetch(`${BQ_BASE_URL}/bq/api/subcategories/`);
        const subCategoriesData = await subCategoriesResponse.json();
        setSubCategories(subCategoriesData);
      } catch (error) {
        toast.error('Failed to fetch subcategories.');
      }
    };

    fetchSubCategories();
  }, []);

  // Add new food item
  const handleAddFoodItem = async () => {
    if (isLoading) return;

    if (
      newFoodItem.name &&
      newFoodItem.description &&
      newFoodItem.price &&
      newFoodItem.image &&
      newFoodItem.subCategoryId &&
      newFoodItem.mealType.length &&
      newFoodItem.foodType
    ) {
      try {
        setIsLoading(true);

        const formData = new FormData();
        formData.append('title', newFoodItem.name);
        formData.append('description', newFoodItem.description);
        formData.append('price', parseFloat(newFoodItem.price));
        formData.append('price_type', newFoodItem.price_type);
        formData.append('image', newFoodItem.image);
        formData.append('foodType', newFoodItem.foodType);
        formData.append('subCategoryId', newFoodItem.subCategoryId);
        formData.append('mealTypes', newFoodItem.mealType);
        formData.append(
          'availableFrom',
          newFoodItem.availableFrom ? newFoodItem.availableFrom + ':00' : '00:00:00'
        );
        formData.append(
          'availableUntil',
          newFoodItem.availableUntil ? newFoodItem.availableUntil + ':00' : '23:59:59'
        );
        formData.append('isActive', newFoodItem.status === 'Available');
        formData.append('showEnhanceStay', newFoodItem.showEnhanceStay);
        formData.append('refundable', newFoodItem.refundable);
        formData.append('showInCheckin', newFoodItem.showInCheckin);
        formData.append('value', newFoodItem.value || 0);
        formData.append('value_type', newFoodItem.value_type || '');

        const response = await fetch(`${BQ_BASE_URL}/bq/api/items/`, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          toast.success('Food item added successfully!');
          setTimeout(() => {
            navigate('/dashboard/mdm/menu-items-managment');
          }, 1500);
        } else {
          toast.error(data.message || 'Failed to add food item.');
        }
      } catch (error) {
        toast.error('An error occurred while adding the food item.');
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.error('Please fill in all fields.');
    }
  };

  // Handle time selection with custom picker
  const handleTimeFocus = (field) => {
    setShowTimePicker({
      ...showTimePicker,
      [field]: true,
    });
    setTempTime({
      ...tempTime,
      [field]: newFoodItem[field] || '12:00',
    });
  };

  const handleTimeChange = (field, value) => {
    setTempTime({
      ...tempTime,
      [field]: value,
    });
  };

  const confirmTimeSelection = (field) => {
    setNewFoodItem({
      ...newFoodItem,
      [field]: tempTime[field],
    });
    setShowTimePicker({
      ...showTimePicker,
      [field]: false,
    });
  };

  const cancelTimeSelection = (field) => {
    setShowTimePicker({
      ...showTimePicker,
      [field]: false,
    });
  };

  // Custom time picker component
  const TimePickerModal = ({ field, onConfirm, onCancel }) => {
    const [selectedTime, setSelectedTime] = useState(tempTime[field] || '12:00');

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

    const formatTime = (hour, minute) => {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`;
    };

    const handleHourChange = (hour) => {
      const [_, minute] = selectedTime.split(':');
      const newTime = `${hour}:${minute || '00'}`;
      setSelectedTime(newTime);
      handleTimeChange(field, newTime);
    };

    const handleMinuteChange = (minute) => {
      const [hour] = selectedTime.split(':');
      const newTime = `${hour}:${minute}`;
      setSelectedTime(newTime);
      handleTimeChange(field, newTime);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl w-80 relative">
          {/* X icon in top-right corner */}
          <button
            onClick={() => onCancel()}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          >
            <FaTimes className="text-lg" />
          </button>

          <h3 className="text-lg font-semibold mb-4 pr-6">
            Select {field === 'availableFrom' ? 'Available From' : 'Available Until'} Time
          </h3>

          <div className="flex justify-between mb-6">
            <div>
              <label className="block text-sm font-medium mb-2">Hour</label>
              <select
                value={selectedTime.split(':')[0]}
                onChange={(e) => handleHourChange(e.target.value)}
                className="p-2 border rounded w-20"
              >
                {hours.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour % 12 || 12}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Minute</label>
              <select
                value={selectedTime.split(':')[1]}
                onChange={(e) => handleMinuteChange(e.target.value)}
                className="p-2 border rounded w-20"
              >
                {minutes.map((minute) => (
                  <option key={minute} value={minute.toString().padStart(2, '0')}>
                    {minute.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">AM/PM</label>
              <select
                value={parseInt(selectedTime.split(':')[0]) >= 12 ? 'PM' : 'AM'}
                onChange={(e) => {
                  const [hour, minute] = selectedTime.split(':');
                  const newHour =
                    e.target.value === 'PM' ? (parseInt(hour) % 12) + 12 : parseInt(hour) % 12;
                  handleHourChange(newHour);
                }}
                className="p-2 border rounded w-20"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          <div className="text-center mb-4">
            <div className="text-2xl font-bold bg-gray-100 p-3 rounded">
              {formatTime(
                parseInt(selectedTime.split(':')[0]),
                parseInt(selectedTime.split(':')[1])
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => onConfirm(selectedTime)}
              className="w-full py-2 bg-black text-white rounded hover:text-white"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <NavLink
        to="/dashboard/mdm/menu-items-managment"
        className="flex items-center text-gray-400 hover:text-black mb-4"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to menu items managment</span>
      </NavLink>

      <div className="bg-white p-6  ">
        <h2 className="text-xl font-semibold mb-4">Add New Food Item</h2>
        <div className="grid grid-cols-2 gap-4">
          {/* Left Column */}
          <div>
            <label className="block text-sm font-semibold mb-1">Food Name</label>
            <input
              type="text"
              value={newFoodItem.name}
              onChange={(e) => setNewFoodItem({ ...newFoodItem, name: e.target.value })}
              placeholder="Enter food item name here."
              className="p-2 border rounded w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Description</label>
            <textarea
              value={newFoodItem.description}
              onChange={(e) =>
                setNewFoodItem({
                  ...newFoodItem,
                  description: e.target.value,
                })
              }
              placeholder="Enter item description here."
              rows={2}
              className="p-2 border rounded w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Price</label>
            <input
              type="number"
              value={newFoodItem.price}
              onChange={(e) => setNewFoodItem({ ...newFoodItem, price: e.target.value })}
              placeholder="Enter item price here."
              className="p-2 border rounded w-full mb-3 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNewFoodItem({ ...newFoodItem, image: e.target.files[0] })}
              className="p-2 border rounded w-full mb-3 text-xs"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Price Type</label>
            <select
              value={newFoodItem.price_type}
              onChange={(e) =>
                setNewFoodItem({
                  ...newFoodItem,
                  price_type: e.target.value,
                })
              }
              className="p-2 border rounded w-full mb-3 text-sm"
            >
              <option value="Per item">Per Item</option>
              <option value="Per person">Per Person</option>
              <option value="Per plate">Per Plate</option>
              <option value="Per pack">Per Pack</option>
              <option value="Per day">Per Day</option>
            </select>
          </div>
        </div>

        {/* 3-Column Grid for the new fields */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          {/* Column 1 */}
          <div>
            <label className="block text-sm font-semibold mb-1">Add Sub-Category</label>
            <select
              value={newFoodItem.subCategoryId}
              onChange={(e) =>
                setNewFoodItem({
                  ...newFoodItem,
                  subCategoryId: e.target.value,
                })
              }
              className="p-2 border rounded w-full mb-1 text-sm"
            >
              <option value="">Select Sub-Category</option>
              {subCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Meal Type</label>
            <div className="flex flex-row gap-2">
              {['Breakfast', 'Lunch', 'Dinner'].map((meal) => (
                <label key={meal} className="inline-flex items-center mt-2 mr-4">
                  <input
                    type="checkbox"
                    checked={newFoodItem.mealType.includes(meal)}
                    onChange={(e) => {
                      const updatedMealTypes = e.target.checked
                        ? [...newFoodItem.mealType, meal]
                        : newFoodItem.mealType.filter((m) => m !== meal);
                      setNewFoodItem({
                        ...newFoodItem,
                        mealType: updatedMealTypes,
                      });
                    }}
                    className="form-checkbox h-5 w-5 text-black"
                  />
                  <span className="ml-2 text-gray-700">{meal}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Food Type</label>
            <select
              value={newFoodItem.foodType}
              onChange={(e) => setNewFoodItem({ ...newFoodItem, foodType: e.target.value })}
              className="p-2 border rounded w-full mb-2 text-sm"
            >
              <option value="Veg">Veg</option>
              <option value="Non-Veg">Non-Veg</option>
            </select>
          </div>

          {/* Column 2 */}
          <div>
            <label className="block text-sm font-semibold mb-1">Status</label>
            <select
              value={newFoodItem.status}
              onChange={(e) => setNewFoodItem({ ...newFoodItem, status: e.target.value })}
              className="p-2 border rounded w-full mb-2 text-sm"
            >
              <option value="Available">Available</option>
              <option value="Not Available">Not Available</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Available From</label>
            <input
              type="text"
              value={
                newFoodItem.availableFrom
                  ? `${newFoodItem.availableFrom.split(':')[0]}:${newFoodItem.availableFrom.split(':')[1]}`
                  : ''
              }
              onFocus={() => handleTimeFocus('availableFrom')}
              readOnly
              className="p-2 border rounded w-full text-sm cursor-pointer"
              placeholder="Select time"
            />
            {showTimePicker.availableFrom && (
              <TimePickerModal
                field="availableFrom"
                onConfirm={(time) => confirmTimeSelection('availableFrom', time)}
                onCancel={() => cancelTimeSelection('availableFrom')}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Available Until</label>
            <input
              type="text"
              value={
                newFoodItem.availableUntil
                  ? `${newFoodItem.availableUntil.split(':')[0]}:${newFoodItem.availableUntil.split(':')[1]}`
                  : ''
              }
              onFocus={() => handleTimeFocus('availableUntil')}
              readOnly
              className="p-2 border rounded w-full text-sm cursor-pointer"
              placeholder="Select time"
            />
            {showTimePicker.availableUntil && (
              <TimePickerModal
                field="availableUntil"
                onConfirm={(time) => confirmTimeSelection('availableUntil', time)}
                onCancel={() => cancelTimeSelection('availableUntil')}
              />
            )}
          </div>

          {/* Column 3 - New Fields */}
          <div>
            <label className="block text-sm font-semibold mb-1">Tax Type</label>
            <select
              value={newFoodItem.value_type}
              onChange={(e) =>
                setNewFoodItem({
                  ...newFoodItem,
                  value_type: e.target.value,
                })
              }
              className="p-2 border rounded w-full mb-2 text-sm"
            >
              <option value="">No Tax</option>
              <option value="P">Percentage (%)</option>
              <option value="F">Fixed Amount (₹)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Tax Value</label>
            <input
              type="number"
              step="0.01"
              value={newFoodItem.value}
              onChange={(e) =>
                setNewFoodItem({
                  ...newFoodItem,
                  value: e.target.value,
                })
              }
              placeholder={newFoodItem.value_type === 'P' ? 'e.g., 5 for 5%' : 'e.g., 10 for ₹10'}
              className="p-2 border rounded w-full mb-2 text-sm"
              disabled={!newFoodItem.value_type}
            />
          </div>

          {/* Empty cell for grid alignment */}
          <div></div>
        </div>

        {/* Feature Toggles in Grid */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          {/* Enhance Stay Toggle */}
          <div>
            <label className="flex justify-between items-start">
              <div>
                <span className="text-gray-700 text-sm font-semibold">Enhance your Stay</span>
                <p className="text-xs text-gray-500 mt-1">
                  Toggle on if this item should be visible to customers during reservation.
                </p>
              </div>
              <div className="relative mt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newFoodItem.showEnhanceStay}
                  onChange={(e) =>
                    setNewFoodItem({
                      ...newFoodItem,
                      showEnhanceStay: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div
                  className={`block w-12 h-6 rounded-full transition ${
                    newFoodItem.showEnhanceStay ? 'bg-black' : 'bg-gray-300'
                  }`}
                ></div>
                <div
                  className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${
                    newFoodItem.showEnhanceStay ? 'translate-x-6' : ''
                  }`}
                ></div>
              </div>
            </label>
          </div>

          {/* Refundable Toggle */}
          <div>
            <label className="flex justify-between items-start">
              <div>
                <span className="text-gray-700 text-sm font-semibold">Refundable</span>
                <p className="text-xs text-gray-500 mt-1">
                  Toggle on if this item can be refunded.
                </p>
              </div>
              <div className="relative mt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newFoodItem.refundable}
                  onChange={(e) =>
                    setNewFoodItem({
                      ...newFoodItem,
                      refundable: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div
                  className={`block w-12 h-6 rounded-full transition ${
                    newFoodItem.refundable ? 'bg-black' : 'bg-gray-300'
                  }`}
                ></div>
                <div
                  className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${
                    newFoodItem.refundable ? 'translate-x-6' : ''
                  }`}
                ></div>
              </div>
            </label>
          </div>

          {/* Show in Check-in Toggle */}
          <div>
            <label className="flex justify-between items-start">
              <div>
                <span className="text-gray-700 text-sm font-semibold">Show in Check-in</span>
                <p className="text-xs text-gray-500 mt-1">
                  Toggle on if this item should be displayed during check-in.
                </p>
              </div>
              <div className="relative mt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newFoodItem.showInCheckin}
                  onChange={(e) =>
                    setNewFoodItem({
                      ...newFoodItem,
                      showInCheckin: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div
                  className={`block w-12 h-6 rounded-full transition ${
                    newFoodItem.showInCheckin ? 'bg-black' : 'bg-gray-300'
                  }`}
                ></div>
                <div
                  className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${
                    newFoodItem.showInCheckin ? 'translate-x-6' : ''
                  }`}
                ></div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={() => navigate('/dashboard/mdm/menu-items-managment')}
            className="px-6 py-2 text-sm bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleAddFoodItem}
            disabled={isLoading}
            className="px-6 py-2 bg-black text-sm hover:text-white text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Adding...
              </>
            ) : (
              'Add Food Item'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddFoodItem;

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { NavLink } from "react-router-dom";
// import { FaArrowLeft } from "react-icons/fa";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { FaTimes } from "react-icons/fa";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// function AddFoodItem() {
//   const navigate = useNavigate();
//   const [subCategories, setSubCategories] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [showTimePicker, setShowTimePicker] = useState({
//     availableFrom: false,
//     availableUntil: false
//   });
//   const [tempTime, setTempTime] = useState({
//     availableFrom: "",
//     availableUntil: ""
//   });
//   const [newFoodItem, setNewFoodItem] = useState({
//     name: "",
//     description: "",
//     price: "",
//     price_type: "Per person",
//     image: null,
//     status: "Available",
//     subCategoryId: "",
//     mealType: [],
//     foodType: "Veg",
//     availableFrom: "",
//     availableUntil: "",
//     showEnhanceStay: false,
//   });

//   // Fetch subcategories from API
//   useEffect(() => {
//     const fetchSubCategories = async () => {
//       try {
//         const subCategoriesResponse = await fetch(
//           `${BQ_BASE_URL}/bq/api/subcategories/`
//         );
//         const subCategoriesData = await subCategoriesResponse.json();
//         setSubCategories(subCategoriesData);
//       } catch (error) {
//         toast.error("Failed to fetch subcategories.");
//       }
//     };

//     fetchSubCategories();
//   }, []);

//   // Add new food item
//   const handleAddFoodItem = async () => {
//     if (isLoading) return;

//     if (
//       newFoodItem.name &&
//       newFoodItem.description &&
//       newFoodItem.price &&
//       newFoodItem.image &&
//       newFoodItem.subCategoryId &&
//       newFoodItem.mealType.length &&
//       newFoodItem.foodType
//     ) {
//       try {
//         setIsLoading(true);

//         const formData = new FormData();
//         formData.append("title", newFoodItem.name);
//         formData.append("description", newFoodItem.description);
//         formData.append("price", parseFloat(newFoodItem.price));
//         formData.append("price_type", newFoodItem.price_type);
//         formData.append("image", newFoodItem.image);
//         formData.append("foodType", newFoodItem.foodType);
//         formData.append("subCategoryId", newFoodItem.subCategoryId);
//         formData.append("mealTypes", newFoodItem.mealType);
//         formData.append(
//           "availableFrom",
//           newFoodItem.availableFrom + "00:00:00"
//         );
//         formData.append(
//           "availableUntil",
//           newFoodItem.availableUntil + "23:59:59"
//         );
//         formData.append("isActive", newFoodItem.status === "Available");
//         formData.append("showEnhanceStay", newFoodItem.showEnhanceStay);

//         const response = await fetch(`${BQ_BASE_URL}/bq/api/items/`, {
//           method: "POST",
//           body: formData,
//         });

//         const data = await response.json();

//         if (response.ok) {
//           toast.success("Food item added successfully!");
//           setTimeout(() => {
//             navigate("/dashboard/mdm/menu-items-managment");
//           }, 1500);
//         } else {
//           toast.error(data.message || "Failed to add food item.");
//         }
//       } catch (error) {
//         toast.error("An error occurred while adding the food item.");
//       } finally {
//         setIsLoading(false);
//       }
//     } else {
//       toast.error("Please fill in all fields.");
//     }
//   };

//   // Handle time selection with custom picker
//   const handleTimeFocus = (field) => {
//     setShowTimePicker({
//       ...showTimePicker,
//       [field]: true
//     });
//     setTempTime({
//       ...tempTime,
//       [field]: newFoodItem[field] || "12:00"
//     });
//   };

//   const handleTimeChange = (field, value) => {
//     setTempTime({
//       ...tempTime,
//       [field]: value
//     });
//   };

//   const confirmTimeSelection = (field) => {
//     setNewFoodItem({
//       ...newFoodItem,
//       [field]: tempTime[field]
//     });
//     setShowTimePicker({
//       ...showTimePicker,
//       [field]: false
//     });

//   };

//   const cancelTimeSelection = (field) => {
//     setShowTimePicker({
//       ...showTimePicker,
//       [field]: false
//     });
//   };

//   // Custom time picker component
//   const TimePickerModal = ({ field, onConfirm, onCancel }) => {
//     const [selectedTime, setSelectedTime] = useState(tempTime[field] || "12:00");

//     const hours = Array.from({ length: 24 }, (_, i) => i);
//     const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

//     const formatTime = (hour, minute) => {
//       const period = hour >= 12 ? "PM" : "AM";
//       const displayHour = hour % 12 || 12;
//       return `${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`;
//     };

//     const handleHourChange = (hour) => {
//       const [_, minute] = selectedTime.split(":");
//       const newTime = `${hour}:${minute || '00'}`;
//       setSelectedTime(newTime);
//       handleTimeChange(field, newTime);
//     };

//     const handleMinuteChange = (minute) => {
//       const [hour] = selectedTime.split(":");
//       const newTime = `${hour}:${minute}`;
//       setSelectedTime(newTime);
//       handleTimeChange(field, newTime);
//     };

//     return (
//       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//         <div className="bg-white p-6 rounded-lg shadow-xl w-80 relative">
//           {/* X icon in top-right corner */}
//           <button
//             onClick={() => onCancel()}
//             className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
//           >
//             <FaTimes className="text-lg" />
//           </button>

//           <h3 className="text-lg font-semibold mb-4 pr-6">
//             Select {field === "availableFrom" ? "Available From" : "Available Until"} Time
//           </h3>

//           <div className="flex justify-between mb-6">
//             <div>
//               <label className="block text-sm font-medium mb-2">Hour</label>
//               <select
//                 value={selectedTime.split(":")[0]}
//                 onChange={(e) => handleHourChange(e.target.value)}
//                 className="p-2 border rounded w-20"
//               >
//                 {hours.map(hour => (
//                   <option key={hour} value={hour}>
//                     {hour % 12 || 12}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div>
//               <label className="block text-sm font-medium mb-2">Minute</label>
//               <select
//                 value={selectedTime.split(":")[1]}
//                 onChange={(e) => handleMinuteChange(e.target.value)}
//                 className="p-2 border rounded w-20"
//               >
//                 {minutes.map(minute => (
//                   <option key={minute} value={minute.toString().padStart(2, '0')}>
//                     {minute.toString().padStart(2, '0')}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div>
//               <label className="block text-sm font-medium mb-2">AM/PM</label>
//               <select
//                 value={parseInt(selectedTime.split(":")[0]) >= 12 ? "PM" : "AM"}
//                 onChange={(e) => {
//                   const [hour, minute] = selectedTime.split(":");
//                   const newHour = e.target.value === "PM"
//                     ? (parseInt(hour) % 12) + 12
//                     : parseInt(hour) % 12;
//                   handleHourChange(newHour);
//                 }}
//                 className="p-2 border rounded w-20"
//               >
//                 <option value="AM">AM</option>
//                 <option value="PM">PM</option>
//               </select>
//             </div>
//           </div>

//           <div className="text-center mb-4">
//             <div className="text-2xl font-bold bg-gray-100 p-3 rounded">
//               {formatTime(parseInt(selectedTime.split(":")[0]), parseInt(selectedTime.split(":")[1]))}
//             </div>
//           </div>

//           <div className="flex justify-end">
//             <button
//               onClick={() => onConfirm(selectedTime)}
//               className="w-full py-2 bg-black text-white rounded hover:text-white"
//             >
//               OK
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen">
//       <ToastContainer
//         position="top-right"
//         autoClose={5000}
//         hideProgressBar={false}
//         newestOnTop={false}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />

//       <NavLink
//         to="/dashboard/mdm/menu-items-managment"
//         className="flex items-center text-gray-400 hover:text-black mb-4"
//       >
//         <FaArrowLeft className="mr-3 text-md" />
//         <span className="text-lg">Back to menu items managment</span>
//       </NavLink>

//       <div className="bg-white p-6  ">
//         <h2 className="text-xl font-semibold mb-4">Add New Food Item</h2>
//         <div className="grid grid-cols-2 gap-4">
//           {/* Left Column - unchanged */}
//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Food Name
//             </label>
//             <input
//               type="text"
//               value={newFoodItem.name}
//               onChange={(e) =>
//                 setNewFoodItem({ ...newFoodItem, name: e.target.value })
//               }
//               placeholder="Enter food item name here."
//               className="p-2 border rounded w-full text-sm"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Description
//             </label>
//             <textarea
//               value={newFoodItem.description}
//               onChange={(e) =>
//                 setNewFoodItem({
//                   ...newFoodItem,
//                   description: e.target.value,
//                 })
//               }
//               placeholder="Enter item description here."
//               rows={2}
//               className="p-2 border rounded w-full text-sm"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-semibold mb-1">Price</label>
//             <input
//               type="number"
//               value={newFoodItem.price}
//               onChange={(e) =>
//                 setNewFoodItem({ ...newFoodItem, price: e.target.value })
//               }
//               placeholder="Enter item price here."
//               className="p-2 border rounded w-full mb-3 text-sm"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-semibold mb-1">Image</label>
//             <input
//               type="file"
//               accept="image/*"
//               onChange={(e) =>
//                 setNewFoodItem({ ...newFoodItem, image: e.target.files[0] })
//               }
//               className="p-2 border rounded w-full mb-3 text-xs"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Price Type
//             </label>
//             <select
//               value={newFoodItem.price_type}
//               onChange={(e) =>
//                 setNewFoodItem({
//                   ...newFoodItem,
//                   price_type: e.target.value,
//                 })
//               }
//               className="p-2 border rounded w-full mb-3 text-sm"
//             >
//               <option value="Per item">Per Item</option>
//               <option value="Per person">Per Person</option>
//               <option value="Per plate">Per Plate</option>
//               <option value="Per pack">Per Pack</option>
//               <option value="Per day">Per Day</option>
//             </select>
//           </div>
//         </div>

//         <div className="grid grid-cols-3 gap-4 mt-4">
//           {/* Right Column */}
//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Add Sub-Category
//             </label>
//             <select
//               value={newFoodItem.subCategoryId}
//               onChange={(e) =>
//                 setNewFoodItem({
//                   ...newFoodItem,
//                   subCategoryId: e.target.value,
//                 })
//               }
//               className="p-2 border rounded w-full mb-1 text-sm"
//             >
//               <option value="">Select Sub-Category</option>
//               {subCategories.map((cat) => (
//                 <option key={cat.id} value={cat.id}>
//                   {cat.name}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Meal Type
//             </label>
//             <div className="flex flex-row gap-2">
//               {["Breakfast", "Lunch", "Dinner"].map((meal) => (
//                 <label
//                   key={meal}
//                   className="inline-flex items-center mt-2 mr-4"
//                 >
//                   <input
//                     type="checkbox"
//                     checked={newFoodItem.mealType.includes(meal)}
//                     onChange={(e) => {
//                       const updatedMealTypes = e.target.checked
//                         ? [...newFoodItem.mealType, meal]
//                         : newFoodItem.mealType.filter((m) => m !== meal);
//                       setNewFoodItem({
//                         ...newFoodItem,
//                         mealType: updatedMealTypes,
//                       });
//                     }}
//                     className="form-checkbox h-5 w-5 text-black"
//                   />
//                   <span className="ml-2 text-gray-700">{meal}</span>
//                 </label>
//               ))}
//             </div>
//           </div>

//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Food Type
//             </label>
//             <select
//               value={newFoodItem.foodType}
//               onChange={(e) =>
//                 setNewFoodItem({ ...newFoodItem, foodType: e.target.value })
//               }
//               className="p-2 border rounded w-full mb-2 text-sm"
//             >
//               <option value="Veg">Veg</option>
//               <option value="Non-Veg">Non-Veg</option>
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm font-semibold mb-1">Status</label>
//             <select
//               value={newFoodItem.status}
//               onChange={(e) =>
//                 setNewFoodItem({ ...newFoodItem, status: e.target.value })
//               }
//               className="p-2 border rounded w-full mb-2 text-sm"
//             >
//               <option value="Available">Available</option>
//               <option value="Not Available">Not Available</option>
//             </select>
//           </div>

//           {/* Available From with custom time picker */}
//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Available From
//             </label>
//             <input
//               type="text"
//               value={newFoodItem.availableFrom ?
//                 `${newFoodItem.availableFrom.split(':')[0]}:${newFoodItem.availableFrom.split(':')[1]}` :
//                 ""
//               }
//               onFocus={() => handleTimeFocus("availableFrom")}
//               readOnly
//               className="p-2 border rounded w-full text-sm cursor-pointer"
//               placeholder="Select time"
//             />
//             {showTimePicker.availableFrom && (
//               <TimePickerModal
//                 field="availableFrom"
//                 onConfirm={(time) => confirmTimeSelection("availableFrom", time)}
//                 onCancel={() => cancelTimeSelection("availableFrom")}
//               />
//             )}
//           </div>

//           {/* Available Until with custom time picker */}
//           <div>
//             <label className="block text-sm font-semibold mb-1">
//               Available Until
//             </label>
//             <input
//               type="text"
//               value={newFoodItem.availableUntil ?
//                 `${newFoodItem.availableUntil.split(':')[0]}:${newFoodItem.availableUntil.split(':')[1]}` :
//                 ""
//               }
//               onFocus={() => handleTimeFocus("availableUntil")}
//               readOnly
//               className="p-2 border rounded w-full text-sm cursor-pointer"
//               placeholder="Select time"
//             />
//             {showTimePicker.availableUntil && (
//               <TimePickerModal
//                 field="availableUntil"
//                 onConfirm={(time) => confirmTimeSelection("availableUntil", time)}
//                 onCancel={() => cancelTimeSelection("availableUntil")}
//               />
//             )}
//           </div>

//           <div className="col-span-3">
//             <label className="flex justify-between items-start ">
//               <div>
//                 <span className="text-gray-700 text-sm font-semibold">
//                   Enhance your Stay
//                 </span>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Toggle on if this item should be visible to customers during
//                   reservation.
//                 </p>
//               </div>
//               <div className="relative mt-1 cursor-pointer">
//                 <input
//                   type="checkbox"
//                   checked={newFoodItem.showEnhanceStay}
//                   onChange={(e) =>
//                     setNewFoodItem({
//                       ...newFoodItem,
//                       showEnhanceStay: e.target.checked,
//                     })
//                   }
//                   className="sr-only"
//                 />
//                 <div
//                   className={`block w-12 h-6 rounded-full transition ${
//                     newFoodItem.showEnhanceStay ? "bg-black" : "bg-gray-300"
//                   }`}
//                 ></div>
//                 <div
//                   className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${
//                     newFoodItem.showEnhanceStay ? "translate-x-6" : ""
//                   }`}
//                 ></div>
//               </div>
//             </label>
//           </div>
//         </div>

//         <div className="flex justify-end gap-4 mt-6">
//           <button
//             onClick={() => navigate("/dashboard/mdm/menu-items-managment")}
//             className="px-6 py-2 text-sm bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors"
//             disabled={isLoading}
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleAddFoodItem}
//             disabled={isLoading}
//             className="px-6 py-2 bg-black text-sm hover:text-white text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
//           >
//             {isLoading ? (
//               <>
//                 <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                 </svg>
//                 Adding...
//               </>
//             ) : (
//               "Add Food Item"
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default AddFoodItem;
